import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMediaQuery } from "react-responsive";
import { Button, Tooltip, Typography } from "antd";
import { ExpandOutlined, CompressOutlined } from "@ant-design/icons";

const { Text } = Typography;

type WindowProps = {
  footerText?: string;
  children?: React.ReactNode; // محتوا (فعلاً می‌تونه خالی باشه)
};

const WindowShell: React.FC<{
  isMaximized: boolean;
  onToggle: () => void;
  isMobile: boolean;
  footerText: string;
  children?: React.ReactNode;
}> = ({ isMaximized, onToggle, isMobile, footerText, children }) => {
  const tooltip = useMemo(
    () => (isMaximized ? "خروج از تمام‌صفحه" : "تمام‌صفحه"),
    [isMaximized]
  );

  return (
    <div
      className="win"
      dir="rtl"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        border: "1px solid #eee",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <header
        className="win__header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          borderBottom: "1px solid #f0f0f0",
          flex: "0 0 auto",
        }}
      >
        <Tooltip title={tooltip}>
          <Button
            type="text"
            className="win__fs-btn"   // مهم برای draggableCancel
            aria-label={tooltip}
            onClick={onToggle}
            icon={isMaximized ? <CompressOutlined /> : <ExpandOutlined />}
          />
        </Tooltip>
      </header>

      {/* Content */}
      <main
        className="win__content"
        style={{
          flex: "1 1 auto",
          minHeight: 0,        // ✅ خیلی مهم برای اینکه overflow درست کار کنه
          overflow: "auto",
          padding: isMobile ? 12 : 16,
        }}
      >
        {children}
      </main>

      {/* Footer */}
      <footer
        className="win__footer"
        style={{
          flex: "0 0 auto",
          padding: isMobile ? "10px 12px" : "10px 16px",
          borderTop: "1px solid #f0f0f0",
          background: "#fafafa",
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          {footerText}
        </Text>
      </footer>
    </div>
  );
};

const Window: React.FC<WindowProps> = ({
  footerText = "",
  children,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [isMaximized, setIsMaximized] = useState(false);

  const toggle = () => setIsMaximized((p) => !p);

  // قفل اسکرول صفحه وقتی تمام‌صفحه شد
  useEffect(() => {
    if (!isMaximized) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMaximized]);

  // خروج با ESC
  useEffect(() => {
    if (!isMaximized) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMaximized(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMaximized]);

  // حالت عادی داخل گرید
  const normal = (
    <WindowShell
      isMaximized={false}
      onToggle={toggle}
      isMobile={isMobile}
      footerText={footerText}
    >
      {children}
    </WindowShell>
  );

  if (!isMaximized) return normal;

  // حالت تمام‌صفحه: Portal به body (تا transform های RGL خرابش نکنن)
  return (
    <>
      {/* placeholder داخل گرید، برای اینکه چیدمان RGL نپره */}
      <div style={{ width: "100%", height: "100%", visibility: "hidden" }}>
        {normal}
      </div>

      {createPortal(
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            padding: isMobile ? 0 : 16,
            background: "rgba(0,0,0,.25)",
            boxSizing: "border-box",
          }}
          onMouseDown={(e) => {
            // کلیک روی بک‌دراپ = بستن (اختیاری)
            if (e.target === e.currentTarget) setIsMaximized(false);
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              borderRadius: isMobile ? 0 : 12,
              overflow: "hidden",
              boxShadow: "0 16px 60px rgba(0,0,0,.25)",
              background: "#fff",
            }}
          >
            <WindowShell
              isMaximized={true}
              onToggle={toggle}
              isMobile={isMobile}
              footerText={footerText}
            >
              {children}
            </WindowShell>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Window;